import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, NgZone } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { AppState, CountryState, ConfigService } from '@fcom/core';
import { isVisible, safeMap, stopPropagation } from '@fcom/utils';
import { keepState } from '@fcom/rx';
import { pseudoLocal } from '@fcom/core/selectors';
import { allowedConfs } from '@fcom/core/reducers/langs';

import { MenuItem, MenuJson, ElementTypes } from '../../../interfaces';
import { NavigationMenuService } from '../../../services';

@Component({
  selector: 'fin-navigation',
  styleUrls: ['./navigation.component.scss'],
  templateUrl: './navigation.component.html',
})
export class NavigationComponent {
  static readonly TRANSITION_DURATION_MS = 300;
  static readonly START_MENU_ITEM_APPEAR_AFTER_MS = 100;
  public readonly ElementTypes = ElementTypes;

  readonly isMenuOpen$: Observable<boolean>;
  readonly menuInDom$: Observable<boolean>;
  readonly homeLink$: Observable<string>;
  readonly menuItems$: Observable<MenuJson>;
  readonly countriesList$: Observable<CountryState[]>;
  readonly currentLanguage$: Observable<string>;
  readonly appearAfter = NavigationComponent.START_MENU_ITEM_APPEAR_AFTER_MS;
  readonly expandedNaviCat$: Observable<MenuItem>;
  readonly _selectLanguageRoot: string = '/';

  insideBooking$: Observable<boolean>;
  countries$: Observable<CountryState>;

  private subscriptions = new Subscription();
  private removeFocusListener: Function;

  constructor(
    private navigationMenuService: NavigationMenuService,
    public configService: ConfigService,
    private languageService: LanguageService,
    private store$: Store<AppState>,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;
    this.menuItems$ = this.navigationMenuService.menuItems$;
    this.homeLink$ = this.languageService.lang.pipe(map((lang) => `/${lang}`));
    this.currentLanguage$ = this.languageService.langKey;
    this.insideBooking$ = this.store$.pipe(pseudoLocal('isInsideBooking'));
    this.countries$ = this.initListOfCountries();
    this.expandedNaviCat$ = navigationMenuService.expandedNaviCategory;
    this.countries$ = this.initListOfCountries();

    this.menuInDom$ = this.isMenuOpen$.pipe(
      keepState(NavigationComponent.TRANSITION_DURATION_MS),
      distinctUntilChanged()
    );

    this.subscriptions.add(
      this.isMenuOpen$.subscribe((isOpen) => {
        if (isOpen) {
          this.bindEventListeners();
        } else {
          this.cleanUpEventHandlers();
        }
      })
    );
  }

  setCategoryAsExpanded(event: Event, item: MenuItem) {
    this.navigationMenuService.setCategoryAsExpanded(event, item);
    this.elementRef.nativeElement.querySelector('.navigation-wrapper').scrollTop = 0;
  }

  closeNaviMenu(): void {
    this.navigationMenuService.closeNaviMenu();
  }

  closeMenu(): void {
    this.navigationMenuService.closeMenu();
  }

  trackItems(index: number) {
    return index;
  }

  private initListOfCountries(): Observable<CountryState> {
    return this.languageService.countryCode.pipe(
      map(
        (countryCode) =>
          allowedConfs.find((country) => country.code === countryCode) || {
            languages: [],
            name: '',
            code: '',
          }
      )
    );
  }

  // @ToDo: create utility to handle focus trapping in a centralized way
  private bindEventListeners(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    this.ngZone.runOutsideAngular(() => {
      const listener = (event) => {
        return self.ngZone.run(() => {
          self.onNaviFocus(event);
        });
      };
      self.document.addEventListener('focus', listener, true);
      self.document.addEventListener('focusin', listener);
      self.removeFocusListener = () => {
        self.document.removeEventListener('focus', listener, true);
        self.document.removeEventListener('focusin', listener);
      };
    });
  }

  private onNaviFocus(event: FocusEvent): void {
    const el = safeMap(this.elementRef, (e) => e.nativeElement);
    if (!!el && !el.contains(event.target)) {
      stopPropagation(event);

      const focusSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const focusableElements: Element[] = Array.from((el as Element).querySelectorAll(focusSelector))
        .filter((e) => e instanceof HTMLElement)
        .filter(isVisible);

      if (focusableElements.length !== 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }

  private cleanUpEventHandlers() {
    if (this.removeFocusListener) {
      this.removeFocusListener();
      this.removeFocusListener = undefined;
    }
  }
}
